// Footer.js
import React from "react";
import { Link } from "react-router-dom";
import {
  Linkedin,
  Instagram,
  Youtube,
  MessageCircle,
  Phone,
  Mail,
  MapPin,
  Clock,
} from "lucide-react";
import { motion } from "framer-motion";
import whiteLogo from "../assets/white_logo.png";

const Footer = () => {
  return (
    <footer className="bg-gray-900 text-white">
      <div className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Top CTA Section */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to start your journey?
            </h2>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
              <Link
                to="/contact"
                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-8 py-3 rounded-lg transition-colors"
              >
                Check your eligibility
              </Link>
              <Link
                to="/contact"
                className="text-white hover:text-blue-300 font-medium underline transition-colors"
              >
                Book a consultation
              </Link>
            </div>

            <p className="text-sm text-gray-300 max-w-2xl mx-auto">
              Information on this site is general guidance and not legal advice.
              Where required by law, we coordinate with licensed local counsel.
            </p>
          </motion.div>

          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Brand Column */}
            <div className="lg:col-span-1">
              <img src={whiteLogo} alt="Visapat" className="h-10 mb-4" />
              <p className="text-gray-400 mb-6 leading-relaxed font-light">
                Immigration consulting for individuals and families. Clear
                steps, compliant applications, and proactive updates.
              </p>
              {/* <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Linkedin size={20} />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Instagram size={20} />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Youtube size={20} />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <MessageCircle size={20} />
                </a>
              </div> */}
            </div>

            {/* Talk to us Column */}
            <div className="lg:col-span-1">
              <h4 className="text-lg font-semibold text-white mb-4">
                Talk to us
              </h4>

              {/* London Office */}
              <div className="mb-6">
                <h5 className="font-medium text-gray-200 mb-2">
                  United Kingdom- EMEA Office
                </h5>
                <div className="text-sm text-gray-400 space-y-1 font-light">
                  <div className="flex items-start">
                    <MapPin size={14} className="mr-2 mt-0.5 flex-shrink-0" />
                    <span>39 George Road, Edgbaston, Birmingham, England, B15 1PL</span>
                  </div>
                  <div className="flex items-center">
                    <Phone size={14} className="mr-2 flex-shrink-0" />
                    <span>+44 (0)75 3716 9698</span>
                  </div>
                  <div className="flex items-center">
                    <Mail size={14} className="mr-2 flex-shrink-0" />
                    <span>emea@visapat.com</span>
                  </div>
                  <div className="flex items-start">
                    <Clock size={14} className="mr-2 mt-0.5 flex-shrink-0" />
                    <span>Mon–Fri, 09:00–18:00 GMT/BST</span>
                  </div>
                </div>
              </div>

              {/* New York Office */}
              <div className="mb-6">
                <h5 className="font-medium text-gray-200 mb-2">
                  New York - APAC Desk
                </h5>
                <div className="text-sm text-gray-400 space-y-1 font-light">
                  <div className="flex items-start">
                    <MapPin size={14} className="mr-2 mt-0.5 flex-shrink-0" />
                    <span>
                      477 Madison Ave 6th floor, New York, NY 10022, United
                      States
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Phone size={14} className="mr-2 flex-shrink-0" />
                    <span>+1 (332) 209-5612</span>
                  </div>
                  <div className="flex items-center">
                    <Mail size={14} className="mr-2 flex-shrink-0" />
                    <span>apac@visapat.com</span>
                  </div>
                  <div className="flex items-start">
                    <Clock size={14} className="mr-2 mt-0.5 flex-shrink-0" />
                    <span>
                      Mon–Fri, 09:00–18:00 ET with evening ET overlap for APAC
                    </span>
                  </div>
                </div>
              </div>

              {/* General Enquiries */}
              <div>
                <h5 className="font-medium text-gray-200 mb-2">
                  General enquiries
                </h5>
                <div className="flex items-center text-sm text-gray-400 font-light">
                  <Mail size={14} className="mr-2 flex-shrink-0" />
                  <span>info@visapat.com</span>
                </div>
              </div>
            </div>

            {/* Services Column */}
            <div className="lg:col-span-1">
              <h4 className="text-lg font-semibold text-white mb-4">
                Services
              </h4>
              <ul className="space-y-2 text-sm text-gray-400 font-light">
                <li>
                  <Link
                    to="/services/skilled-worker-employment-visas"
                    className="hover:text-white transition-colors"
                  >
                    Skilled Worker and Employment
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services/study-permits-graduate-pathways"
                    className="hover:text-white transition-colors"
                  >
                    Study Permits and Graduate Pathways
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services/family-and-dependent-visas"
                    className="hover:text-white transition-colors"
                  >
                    Family and Dependent Visas
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services/permanent-residence-and-citizenship"
                    className="hover:text-white transition-colors"
                  >
                    Permanent Residence and Citizenship
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services/investor-and-entrepreneur-routes"
                    className="hover:text-white transition-colors"
                  >
                    Investor and Entrepreneur Routes
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services/visitor-and-short-stay"
                    className="hover:text-white transition-colors"
                  >
                    Visitor and Short-Stay
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services/appeals-and-complex-cases"
                    className="hover:text-white transition-colors"
                  >
                    Appeals and Complex Cases
                  </Link>
                </li>
              </ul>
            </div>

            {/* Support Column */}
            <div className="lg:col-span-1">
              <h4 className="text-lg font-semibold text-white mb-4">Support</h4>
              <ul className="space-y-2 text-sm text-gray-400 font-light">
                <li>
                  <Link
                    to="/privacy-policy"
                    className="hover:text-white transition-colors"
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms-of-service"
                    className="hover:text-white transition-colors"
                  >
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link
                    to="/cookie-policy"
                    className="hover:text-white transition-colors"
                  >
                    Cookie Policy
                  </Link>
                </li>
                {/* <li>
                  <Link to="/accessibility" className="hover:text-white transition-colors">
                    Accessibility
                  </Link>
                </li> */}
                {/* <li>
                  <Link to="/complaints" className="hover:text-white transition-colors">
                    Complaints
                  </Link>
                </li> */}
                {/* <li>
                  <Link to="/sitemap" className="hover:text-white transition-colors">
                    Sitemap
                  </Link>
                </li> */}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Legal Bar */}
      {/* <div className="border-t border-gray-700 py-6 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div className="text-xs text-gray-400 font-light">
              <p className="mb-2"> 2025 Visapat. All rights reserved.</p>
              <p className="mb-2">
                We are an immigration consulting practice and not a law firm. We are not affiliated with any government.
              </p>
              <p className="mb-2">
                Data controller: Visapat [legal entity name]. Contact: privacy@visapat.com
              </p>
              <p>
                Registered office: [full legal address]. Company number: [number].
              </p>
            </div>
            <button className="text-xs text-gray-400 hover:text-white transition-colors underline font-light">
              Cookie settings
            </button>
          </div>
        </div>
      </div> */}
    </footer>
  );
};

export default Footer;
